import { useState, useEffect, useCallback, useRef } from 'react';
import { req } from '../utils';

// Global cache to prevent duplicate requests
const dataCache = new Map();
const requestPromises = new Map();

export const useDataFetcher = (endpoint, params = {}, options = {}) => {
  const {
    interval = null, // null means no interval
    cacheTime = 5 * 60 * 1000, // 5 minutes default
    enabled = true,
    onSuccess,
    onError
  } = options;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const intervalRef = useRef(null);
  const lastFetchRef = useRef(0);

  // Create a cache key based on endpoint and params
  const cacheKey = useCallback(() => {
    const paramString = JSON.stringify(params);
    return `${endpoint}:${paramString}`;
  }, [endpoint, params]);

  const fetchData = useCallback(async (force = false) => {
    const key = cacheKey();
    const now = Date.now();

    // Check if we have valid cached data
    const cached = dataCache.get(key);
    if (!force && cached && (now - cached.timestamp) < cacheTime) {
      setData(cached.data);
      setLoading(false);
      setError(null);
      return;
    }

    // Check if there's already a request in progress
    if (requestPromises.has(key)) {
      try {
        const result = await requestPromises.get(key);
        setData(result);
        setLoading(false);
        setError(null);
        if (onSuccess) onSuccess(result);
      } catch (err) {
        setError(err);
        setLoading(false);
        if (onError) onError(err);
      }
      return;
    }

    // Start new request
    setLoading(true);
    setError(null);

    const requestPromise = req('GET', endpoint, params)
      .then(result => {
        // Cache the result
        dataCache.set(key, {
          data: result,
          timestamp: now
        });
        
        setData(result);
        setLoading(false);
        if (onSuccess) onSuccess(result);
        return result;
      })
      .catch(err => {
        setError(err);
        setLoading(false);
        if (onError) onError(err);
        throw err;
      })
      .finally(() => {
        requestPromises.delete(key);
      });

    requestPromises.set(key, requestPromise);
  }, [endpoint, params, cacheTime, onSuccess, onError, cacheKey]);

  // Set up interval if specified
  useEffect(() => {
    if (!enabled || !interval) return;

    const startInterval = () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(() => {
        fetchData(true); // Force refresh
      }, interval);
    };

    // Initial fetch
    fetchData();
    lastFetchRef.current = Date.now();

    // Start interval
    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, interval, fetchData]);

  // Fetch on mount and when dependencies change (but not for intervals)
  useEffect(() => {
    if (!enabled || interval) return; // Skip if interval is set or disabled
    
    fetchData();
  }, [enabled, fetchData]);

  // Manual refresh function
  const refresh = useCallback(() => {
    fetchData(true);
  }, [fetchData]);

  return {
    data,
    loading,
    error,
    refresh
  };
};

// Utility function to clear cache
export const clearDataCache = () => {
  dataCache.clear();
  requestPromises.clear();
};

// Utility function to clear specific cache entry
export const clearCacheEntry = (endpoint, params = {}) => {
  const key = `${endpoint}:${JSON.stringify(params)}`;
  dataCache.delete(key);
}; 